import { ReactNode } from "react";
import { Dialog, DialogContent, DialogProps, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Enlarged copy of a chart or table (the "fullscreen" button of a card). Not a
 * dialog in the UX sense: nothing is asked or decided, the user is only
 * looking closer. Centred rather than edge-to-edge on purpose: full-bleed
 * inflates chrome at the expense of the data. No title, no footer: the
 * content keeps its own header, so the only chrome is the close cross in the
 * corner. Does not go fullScreen on small screens and never syncs with the
 * URL, which is why it is not a ResponsiveDialog.
 */
const ExpandedView = ({
  open,
  onClose,
  maxWidth = "xl",
  // Fixed paper height (e.g. "60vh") for content that fills its box; omit
  // for content that sizes itself (tables).
  paperHeight,
  padded = false,
  "aria-labelledby": ariaLabelledBy,
  children,
}: {
  open: boolean;
  onClose: () => void;
  maxWidth?: DialogProps["maxWidth"];
  paperHeight?: string;
  // Narrow padding for content that already carries its own card spacing.
  padded?: boolean;
  "aria-labelledby"?: string;
  children: ReactNode;
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={maxWidth}
    fullWidth
    aria-labelledby={ariaLabelledBy}
    slotProps={{
      paper: { sx: paperHeight ? { height: paperHeight } : undefined },
    }}
  >
    <IconButton
      aria-label="close fullscreen"
      onClick={onClose}
      sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
    >
      <CloseIcon />
    </IconButton>
    <DialogContent
      sx={
        padded
          ? { p: 1 }
          : { display: "flex", flexDirection: "column", minHeight: 0 }
      }
    >
      {children}
    </DialogContent>
  </Dialog>
);

export default ExpandedView;
